import React, { useState, useEffect } from 'react';
import { Search, Download, UserX, LogOut, RefreshCcw, RotateCcw, Info, Fingerprint, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import { CORES, STATUS, BadgeStatus, Botao, CartaoKpi, estilos } from '../psUI';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// =============================================================================
// Banco de Talentos — Reprovados + Desistentes numa tela só (fusão 2026-06-30)
// =============================================================================
// Antes eram duas telas quase iguais. Aqui unificamos: um filtro de TIPO
// (Todos | Reprovados | Desistentes) + coluna Tipo com badge semântico. A ação
// "Restaurar" (volta o candidato à fila de entrevista) fica só para DESISTENTES.
//
// ⚠️ A classificação reprovado x desistente usa a MESMA lógica do Monitoramento
// (a "fonte da verdade" do módulo) — antes esta tela tinha uma lógica própria mais
// simples que divergia (ex.: jogava reprovados na aba Desistentes e poluía o filtro
// de Motivo). Manter as duas telas idênticas evita essa inconsistência.

// Categoriza um candidato igual ao Monitoramento. Retorna REPROVADOS | DESISTENTES |
// ATIVOS | APROVADOS. Aqui só nos interessam REPROVADOS e DESISTENTES.
function categorizar(cand) {
    const origem = cand.ORIGEM;
    const status = Number(cand.STATUS_ETAPA);
    if (origem === 'Inscricao') {
        if (status === 0) return 'REPROVADOS';
        if (status === 1) return 'ATIVOS';
        if (status === 3) {
            const motivo = (cand.MOTIVO_REPROVACAO || '').toUpperCase();
            // "COMPAREC" (Não compareceu ao Exame) também conta como desistência — e como o
            // dedup costuma manter ESTA linha de Inscrição, o check precisa estar aqui também.
            if (motivo.includes('DESIST') || motivo.includes('COMPAREC')) return 'DESISTENTES';
            return 'REPROVADOS';
        }
    } else {
        if (status === 0) {
            const motivo = (cand.MOTIVO_REPROVACAO || '').toUpperCase();
            // "COMPAREC" cobre "Não compareceu ao Exame" — tratado como desistência
            if (motivo.includes('DESIST') || motivo.includes('COMPAREC')) return 'DESISTENTES';
            return 'REPROVADOS';
        }
        if (status === 8) return 'DESISTENTES';
        if (status === 7) return 'APROVADOS';
        if (status >= 2 && status <= 6) return 'ATIVOS';
    }
    return 'ATIVOS';
}

const ProcessoSeletivoBancoTalentos = () => {
    const [candidatos, setCandidatos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Filtros
    const [filtroTipo, setFiltroTipo] = useState('todos'); // todos | reprovado | desistente
    const [filterNome, setFilterNome] = useState('');
    const [filterCpf, setFilterCpf] = useState('');
    const [filterData, setFilterData] = useState('');
    const [filterVaga, setFilterVaga] = useState('');
    const [filterMotivo, setFilterMotivo] = useState('');

    // Paginação (50 por página)
    const [paginaAtual, setPaginaAtual] = useState(1);
    const ITENS_POR_PAGINA = 50;

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : {};
    const canAction = ['Admin', 'RH', 'Diretoria', 'ProcessoSeletivo'].includes(perfilNoModulo(user.perfil, 'processoseletivo'));

    // Ao trocar o Tipo (Todos/Reprovados/Desistentes), zera o Motivo selecionado —
    // senão um motivo válido só p/ reprovados ficaria preso ao mudar p/ desistentes.
    useEffect(() => { setFilterMotivo(''); }, [filtroTipo]);

    // Qualquer mudança de filtro volta para a 1ª página (senão ficaria numa página vazia).
    useEffect(() => { setPaginaAtual(1); }, [filtroTipo, filterNome, filterCpf, filterData, filterVaga, filterMotivo]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = localStorage.getItem('authToken');
                const response = await fetch('/api/processo-seletivo/candidatos', { headers: { 'Authorization': `Bearer ${token}` } });
                if (!response.ok) throw new Error("Erro ao carregar os dados.");
                setCandidatos(await response.json());
            } catch (err) { setError(err.message); }
            finally { setIsLoading(false); }
        };
        fetchData();
    }, []);

    // 1) Dedup por CPF — MESMA regra do Monitoramento: prefere a linha de Inscrição
    // que NÃO está arquivada (status 99); se só existe a de Processo, usa ela.
    const dedupMap = new Map();
    candidatos.forEach(cand => {
        const chave = cand.CPF || cand.ID;
        const existente = dedupMap.get(chave);
        if (!existente) { dedupMap.set(chave, cand); return; }
        if (cand.ORIGEM === 'Inscricao' && Number(cand.STATUS_ETAPA) !== 99) {
            dedupMap.set(chave, cand);
        } else if (cand.ORIGEM === 'Processo' && existente.ORIGEM === 'Inscricao' && Number(existente.STATUS_ETAPA) === 99) {
            dedupMap.set(chave, cand);
        }
    });

    // 2) Da lista deduplicada, fica só quem é REPROVADO ou DESISTENTE (com o _tipo).
    const base = Array.from(dedupMap.values())
        .map(cand => ({ ...cand, _tipo: categorizar(cand) === 'DESISTENTES' ? 'desistente' : 'reprovado', _categoria: categorizar(cand) }))
        .filter(cand => cand._categoria === 'REPROVADOS' || cand._categoria === 'DESISTENTES');

    // Texto do motivo de um candidato (desistente sem motivo explícito = "Desistência").
    // Normaliza o legado 'Histórico' → 'Reprovação por Histórico' (mesmo motivo, grafia antiga),
    // pra não aparecer duas opções iguais no filtro mesmo antes de a migration rodar.
    const motivoDe = (cand) => {
        const m = (cand.MOTIVO_REPROVACAO || (cand._tipo === 'desistente' ? 'Desistência' : 'Não informado')).trim();
        return m === 'Histórico' ? 'Reprovação por Histórico' : m;
    };

    // Autoria da desistência (tooltip do badge): quem tirou o candidato do funil e quando.
    // Só existe para quem desistiu PELO PORTAL a partir de 2026-07-14 (data das colunas
    // DESISTENCIA_*) — registro antigo não tem o rastro e fica sem tooltip.
    const autoriaDe = (cand) => {
        if (!cand.DESISTENCIA_USUARIO_NOME && !cand.DESISTENCIA_DATA) return undefined;
        const quando = cand.DESISTENCIA_DATA ? new Date(cand.DESISTENCIA_DATA).toLocaleString('pt-BR') : 'data não registrada';
        return `Desistência registrada por ${cand.DESISTENCIA_USUARIO_NOME || 'usuário não identificado'} em ${quando}`;
    };

    // Opções dos selects — DINÂMICAS (saem dos dados reais), e o Motivo respeita o Tipo
    // selecionado: só lista motivos que de fato existem para reprovados/desistentes/todos.
    const vagasDisponiveis = [...new Set(base.map(c => c.VAGA_SELECIONADA || c.CARGO).filter(Boolean))].sort();
    const motivosDisponiveis = [...new Set(
        base
            .filter(c => filtroTipo === 'todos' || c._tipo === filtroTipo)
            .map(motivoDe)
            .filter(Boolean)
    )].sort();

    const lista = base.filter(cand => {
        // Tipo
        if (filtroTipo !== 'todos' && cand._tipo !== filtroTipo) return false;
        // Nome
        if (filterNome && !cand.NOME_COMPLETO?.toLowerCase().includes(filterNome.toLowerCase())) return false;
        // CPF
        const cpfLimpo = cand.CPF ? String(cand.CPF).replace(/\D/g, '') : '';
        if (filterCpf && !cpfLimpo.includes(filterCpf)) return false;
        // Data (sempre pela DATA_INSCRICAO, presente para todos)
        if (filterData) {
            if (!cand.DATA_INSCRICAO) return false;
            const d = new Date(cand.DATA_INSCRICAO).toISOString().split('T')[0];
            if (d !== filterData) return false;
        }
        // Vaga
        const vagaCand = cand.VAGA_SELECIONADA || cand.CARGO;
        if (filterVaga && vagaCand !== filterVaga) return false;
        // Motivo — match exato (as opções vêm dos dados reais, então sempre batem)
        if (filterMotivo && motivoDe(cand) !== filterMotivo) return false;
        return true;
    });

    const qtdReprovados = base.filter(c => c._tipo === 'reprovado').length;
    const qtdDesistentes = base.filter(c => c._tipo === 'desistente').length;

    // Recorte da página atual (50 por vez)
    const totalPaginas = Math.ceil(lista.length / ITENS_POR_PAGINA);
    const indexPrimeiro = (paginaAtual - 1) * ITENS_POR_PAGINA;
    const listaPaginada = lista.slice(indexPrimeiro, indexPrimeiro + ITENS_POR_PAGINA);

    // Reprovado AUTOMATICAMENTE (por tempo/inatividade) — pode voltar ao início (Triagem).
    const isAutoReprovado = (cand) => cand._tipo === 'reprovado' && (cand.MOTIVO_REPROVACAO || '').startsWith('Reprovação Automática');

    const handleReativar = async (cand) => {
        if (!window.confirm(`Reativar ${cand.NOME_COMPLETO}? Ele volta para a Triagem (início do processo).`)) return;
        try {
            const token = localStorage.getItem('authToken');
            const resp = await fetch('/api/processo-seletivo/reativar-triagem', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ cpf: cand.CPF }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Erro ao reativar.');
            alert("Candidato reativado! Voltou para a Triagem.");
            setCandidatos(prev => prev.filter(c => c.CPF !== cand.CPF));
        } catch (e) { alert(e.message); }
    };

    const handleRestaurar = async (idCandidato) => {
        if (!window.confirm("Deseja restaurar este candidato para a fila de entrevista?")) return;
        try {
            const token = localStorage.getItem('authToken');
            await fetch('/api/processo-seletivo/retornar-fila-entrevista', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ idCandidato }),
            });
            alert("Candidato restaurado com sucesso!");
            setCandidatos(prev => prev.filter(c => c.ID !== idCandidato));
        } catch (e) { alert("Erro ao restaurar o candidato."); }
    };

    const exportarExcel = () => {
        const dados = lista.map(cand => ({
            'Tipo': cand._tipo === 'desistente' ? 'Desistente' : 'Reprovado',
            'Data da Inscrição': cand.DATA_INSCRICAO ? new Date(cand.DATA_INSCRICAO).toLocaleDateString('pt-BR') : '-',
            'Nome Completo': cand.NOME_COMPLETO,
            'CPF': cand.CPF,
            'Celular': cand.CELULAR,
            'Vaga': cand.VAGA_SELECIONADA || cand.CARGO || 'Não informada',
            'Motivo': cand.MOTIVO_REPROVACAO || (cand._tipo === 'desistente' ? 'Desistência' : 'Não informado'),
        }));
        const ws = XLSX.utils.json_to_sheet(dados);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Banco de Talentos");
        XLSX.writeFile(wb, "Banco_de_Talentos.xlsx");
    };

    if (isLoading) return <div style={{ padding: '40px', textAlign: 'center', color: CORES.textoFraco }}>Carregando dados...</div>;
    if (error) return <div style={{ padding: '24px', textAlign: 'center', color: CORES.primaria }}>{error}</div>;

    // Botão de segmento (Tipo)
    const SegBtn = ({ valor, label, icone: Icone }) => {
        const ativo = filtroTipo === valor;
        return (
            <button onClick={() => setFiltroTipo(valor)} style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                borderRadius: '999px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                border: ativo ? `2px solid ${CORES.primaria}` : `1px solid ${CORES.borda}`,
                backgroundColor: ativo ? CORES.primariaBg : '#fff',
                color: ativo ? CORES.primaria : CORES.textoFraco,
            }}>
                {Icone && <Icone size={15} />} {label}
            </button>
        );
    };

    return (
        <div style={{ animation: 'fadeIn 0.3s ease-in-out' }}>
            {/* Cabeçalho */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                <div>
                    <h2 style={estilos.tituloSecao}>Banco de Talentos</h2>
                    <p style={estilos.subtituloSecao}>Candidatos reprovados ou que desistiram do processo. Mostrando {lista.length} registro(s).</p>
                </div>
                <Botao variant="sucesso" icone={Download} onClick={exportarExcel}>Exportar XLSX</Botao>
            </div>

            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '14px', marginBottom: '20px' }}>
                <CartaoKpi titulo="Total" valor={base.length} cor={CORES.primaria} />
                <CartaoKpi titulo="Reprovados" valor={qtdReprovados} cor={STATUS.reprovado.fg} icone={UserX} />
                <CartaoKpi titulo="Desistentes" valor={qtdDesistentes} cor={STATUS.desistente.fg} icone={LogOut} />
            </div>

            {/* Segmento de Tipo */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <SegBtn valor="todos" label="Todos" />
                <SegBtn valor="reprovado" label="Reprovados" icone={UserX} />
                <SegBtn valor="desistente" label="Desistentes" icone={LogOut} />
            </div>

            {/* Filtros */}
            <div style={estilos.barraFiltros}>
                <div>
                    <label style={estilos.rotuloFiltro}>Nome</label>
                    <div style={{ position: 'relative' }}>
                        <Search size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '11px' }} />
                        <input type="text" placeholder="Buscar por nome..." value={filterNome} onChange={(e) => setFilterNome(e.target.value)} style={{ ...estilos.input, paddingLeft: '36px' }} />
                    </div>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>CPF</label>
                    <div style={{ position: 'relative' }}>
                        <Fingerprint size={16} color="#94a3b8" style={{ position: 'absolute', left: '12px', top: '11px' }} />
                        <input type="text" placeholder="Apenas números..." value={filterCpf} onChange={(e) => setFilterCpf(e.target.value.replace(/\D/g, ''))} style={{ ...estilos.input, paddingLeft: '36px' }} />
                    </div>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Data da Inscrição</label>
                    <input type="date" value={filterData} onChange={(e) => setFilterData(e.target.value)} style={estilos.input} />
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Vaga</label>
                    <select value={filterVaga} onChange={(e) => setFilterVaga(e.target.value)} style={{ ...estilos.input, cursor: 'pointer' }}>
                        <option value="">Todas as Vagas</option>
                        {vagasDisponiveis.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                </div>
                <div>
                    <label style={estilos.rotuloFiltro}>Motivo</label>
                    <select value={filterMotivo} onChange={(e) => setFilterMotivo(e.target.value)} style={{ ...estilos.input, cursor: 'pointer' }}>
                        <option value="">Todos os Motivos</option>
                        {motivosDisponiveis.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
            </div>

            {/* Tabela */}
            <div style={estilos.tabelaWrap}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={estilos.tabela}>
                        <thead>
                            <tr>
                                <th style={estilos.th}>Data</th>
                                <th style={estilos.th}>Nome e Contato</th>
                                <th style={estilos.th}>Vaga</th>
                                <th style={estilos.th}>Motivo</th>
                                <th style={{ ...estilos.th, textAlign: 'center' }}>Tipo</th>
                                {canAction && <th style={{ ...estilos.th, textAlign: 'center' }}>Ação</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {listaPaginada.length > 0 ? listaPaginada.map(cand => (
                                <tr key={cand.ID}>
                                    <td style={{ ...estilos.td, color: CORES.textoSec, whiteSpace: 'nowrap' }}>{cand.DATA_INSCRICAO ? new Date(cand.DATA_INSCRICAO).toLocaleDateString('pt-BR') : '-'}</td>
                                    <td style={estilos.td}>
                                        <div style={{ fontWeight: 700, color: CORES.texto }}>{cand.NOME_COMPLETO}</div>
                                        <div style={{ color: CORES.textoFraco, fontSize: '12px' }}>{cand.CELULAR}</div>
                                    </td>
                                    <td style={{ ...estilos.td, color: CORES.textoSec }}>{cand.VAGA_SELECIONADA || cand.CARGO || 'Não especificada'}</td>
                                    <td style={estilos.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Info size={15} color={CORES.textoFraco} />
                                            {motivoDe(cand)}
                                        </div>
                                    </td>
                                    <td style={{ ...estilos.td, textAlign: 'center' }}>
                                        {cand._tipo === 'desistente'
                                            ? <BadgeStatus tom="desistente" icone={LogOut} title={autoriaDe(cand)}>Desistente</BadgeStatus>
                                            : <BadgeStatus tom="reprovado" icone={UserX}>Reprovado</BadgeStatus>}
                                    </td>
                                    {canAction && (
                                        <td style={{ ...estilos.td, textAlign: 'center' }}>
                                            {cand._tipo === 'desistente'
                                                ? <Botao variant="primario" icone={RefreshCcw} style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => handleRestaurar(cand.ID)}>Restaurar</Botao>
                                                : isAutoReprovado(cand)
                                                    ? <Botao variant="secundario" icone={RotateCcw} style={{ padding: '6px 12px', fontSize: '12px' }} onClick={() => handleReativar(cand)} title="Voltar para a Triagem (reprovado automaticamente)">Reativar</Botao>
                                                    : <span style={{ color: '#cbd5e1' }}>—</span>}
                                        </td>
                                    )}
                                </tr>
                            )) : (
                                <tr><td colSpan={canAction ? 6 : 5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nenhum registro encontrado.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginação (50 por página) */}
                {totalPaginas > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: `1px solid ${CORES.borda}`, backgroundColor: CORES.bgSuave }}>
                        <span style={{ fontSize: '14px', color: CORES.textoFraco }}>
                            Mostrando {indexPrimeiro + 1} a {Math.min(indexPrimeiro + ITENS_POR_PAGINA, lista.length)} de {lista.length}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button onClick={() => setPaginaAtual(p => Math.max(p - 1, 1))} disabled={paginaAtual === 1} style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${CORES.borda}`, backgroundColor: paginaAtual === 1 ? '#f1f5f9' : '#fff', color: paginaAtual === 1 ? '#cbd5e1' : CORES.texto, cursor: paginaAtual === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                <ChevronLeft size={18} />
                            </button>
                            <span style={{ padding: '8px 16px', fontSize: '14px', fontWeight: 600, color: CORES.texto }}>Página {paginaAtual} de {totalPaginas}</span>
                            <button onClick={() => setPaginaAtual(p => Math.min(p + 1, totalPaginas))} disabled={paginaAtual === totalPaginas} style={{ padding: '8px 12px', borderRadius: '8px', border: `1px solid ${CORES.borda}`, backgroundColor: paginaAtual === totalPaginas ? '#f1f5f9' : '#fff', color: paginaAtual === totalPaginas ? '#cbd5e1' : CORES.texto, cursor: paginaAtual === totalPaginas ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProcessoSeletivoBancoTalentos;
